import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CatcherAccuracy, Dropdown } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { usePitchVideoFn } from '../data';

const StyledContainer = styled.div`
    display: inline-block;
`;
const StyledFiltersContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    > div {
        display: flex;
    }
    > div > * {
        margin: 5px;
    }
`;
const StyledFiltersLabel = styled.div`
    font-size: 12px;
    font-weight: bold;
    margin-top: auto;
    margin-bottom: auto;
    padding: 0 10px;
`;

const VIEW = { B2: 2, B3: 3 };

const filterThrows = (throws, base, filters) => {
    const { time, runners, situation, result, dirtball, batter, count } = filters;
    const filteredThrows = throws.filter((t) => t.base === base
        && (!time.filterFn || time.filterFn(t))
        && (!runners.filterFn || runners.filterFn(t))
        && (!situation.filterFn || situation.filterFn(t))
        && (!result.filterFn || result.filterFn(t))
        && (!dirtball.filterFn || dirtball.filterFn(t))
        && (!batter.filterFn || batter.filterFn(t))
        && (!count.filterFn || count.filterFn(t)));
    return filteredThrows;
};

const makeSeries = (translate, throws, base) => {
    const defaultSeries = base === VIEW.B2 ? [
        { data: [], name: '<1.82', index: 1, id: 'set1', color: '#cc0000', threshold: 1.82 },
        { data: [], name: '1.82-1.90', index: 2, id: 'set2', color: '#dddd00', threshold: 1.90 },
        { data: [], name: '1.90-1.98', index: 3, id: 'set3', color: '#009900', threshold: 1.98 },
        { data: [], name: '1.98-2.06', index: 4, id: 'set4', color: '#0000cc', threshold: 2.06 },
        { data: [], name: '2.06+', index: 5, id: 'set5', color: '#aa00aa', threshold: 999 },
    ] : [
        { data: [], name: '<1.40', index: 1, id: 'set1', color: '#cc0000', threshold: 1.401 },
        { data: [], name: '1.40-1.46', index: 2, id: 'set2', color: '#dddd00', threshold: 1.468 },
        { data: [], name: '1.46-1.50', index: 3, id: 'set3', color: '#009900', threshold: 1.502 },
        { data: [], name: '1.50-1.56', index: 4, id: 'set4', color: '#0000cc', threshold: 1.568 },
        { data: [], name: '1.56+', index: 5, id: 'set5', color: '#aa00aa', threshold: 999 },
    ];

    let series = defaultSeries;

    for (let i = 0; i < throws.length; ++i) {
        if (throws[i].base === base && throws[i].xcoordfromcatcher && throws[i].ycoordfromcatcher && throws[i].catchertime) {
            const time = throws[i].catchertime;
            const point = {
                x: throws[i].xcoordfromcatcher,
                y: (500 - throws[i].ycoordfromcatcher),
                name: '',
                tooltip: `${throws[i].type} ${throws[i].base}B, ${time
                }<br />${throws[i].istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `}${throws[i].inning}, ${
                    throws[i].outsbefore} ${translate('baseball.outs', { count: 1 })}, ${
                    throws[i].balls}-${throws[i].strikes
                }<br />${translate('baseball.runners', { count: 1 })}: ${throws[i].runnername} (${throws[i].battingteamabbr})`,
                url: throws[i].url,
                gameid: throws[i].gameid,
                eventseq: throws[i].eventseq,
                pitchseq: throws[i].pitchseq,
            };

            for (let s = 0; s < series.length; s++) {
                if (time < series[s].threshold) {
                    series[s].data.push(point);
                    break;
                }
            }
        }
    }

    series = series.filter((x) => x.data.length > 0);
    if (series.length === 0) return null;
    return series;
};

const timeOptions = (translate) => [
    { name: translate('common.all'), filterFn: null },
    { name: translate('components.catcher-accuracy-chart.quick'), filterFn: (x) => x.catchertime < 1.90 },
    { name: translate('components.catcher-accuracy-chart.average'), filterFn: (x) => x.catchertime >= 1.90 && x.catchertime <= 1.98 },
    { name: translate('components.catcher-accuracy-chart.slow'), filterFn: (x) => x.catchertime > 1.98 },
];

const runnersOptions = (translate) => [
    { name: translate('common.all'), filterFn: null, base: null },
    { name: translate('format.number_ordinal_one', { count: 1 }), filterFn: (x) => x.bcbefore === 1, base: 2 },
    { name: translate('format.number_ordinal_two', { count: 2 }), filterFn: (x) => x.bcbefore === 2, base: 3 },
    { name: `${translate('format.number_ordinal_one', { count: 1 })} & ${translate('format.number_ordinal_two', { count: 2 })}`, filterFn: (x) => x.bcbefore === 3, base: null },
    { name: `${translate('format.number_ordinal_one', { count: 1 })} & ${translate('format.number_ordinal_few', { count: 3 })}`, filterFn: (x) => x.bcbefore === 5, base: 2 },
];

const situationOptions = (translate) => [
    { name: translate('common.all'), filterFn: null },
    { name: translate('components.catcher-accuracy-chart.double-steal'), filterFn: (x) => x.playtype === 'Double Steal' },
    { name: translate('components.catcher-accuracy-chart.straight'), filterFn: (x) => x.playtype === 'Straight' },
    { name: translate('components.catcher-accuracy-chart.delay'), filterFn: (x) => x.playtype === 'Delay' },
];

const resultOptions = (translate) => [
    { name: translate('common.all'), filterFn: null },
    { name: translate('components.catcher-accuracy-chart.caught'), filterFn: (x) => x.fieldercatch === 'Catch' || x.fieldercatch === 'Pick' },
    { name: translate('components.catcher-accuracy-chart.not-caught'), filterFn: (x) => x.fieldercatch !== 'Catch' && x.fieldercatch !== 'Pick' },
];

const dirtballOptions = (translate) => [
    { name: translate('common.all'), filterFn: null },
    { name: translate('components.catcher-accuracy-chart.dirtball'), filterFn: (x) => x.pitchindirt },
    { name: translate('components.catcher-accuracy-chart.dirtball-non'), filterFn: (x) => !x.pitchindirt },
];

const batterOptions = (translate) => [
    { name: translate('common.all'), filterFn: null },
    { name: translate('baseball.lhh'), filterFn: (x) => x.islhb },
    { name: translate('baseball.rhh'), filterFn: (x) => !x.islhb },
];

const countOptions = (translate) => [
    { name: translate('common.all'), filterFn: null },
    { name: '0-0', filterFn: (x) => x.balls === 0 && x.strikes === 0 },
    { name: '1-0', filterFn: (x) => x.balls === 1 && x.strikes === 0 },
    { name: '1-1', filterFn: (x) => x.balls === 1 && x.strikes === 1 },
    { name: '2-0', filterFn: (x) => x.balls === 2 && x.strikes === 0 },
    { name: '2-1', filterFn: (x) => x.balls === 2 && x.strikes === 1 },
    { name: '2-2', filterFn: (x) => x.balls === 2 && x.strikes === 2 },
    { name: '3-0', filterFn: (x) => x.balls === 3 && x.strikes === 0 },
    { name: '3-1', filterFn: (x) => x.balls === 3 && x.strikes === 1 },
    { name: '3-2', filterFn: (x) => x.balls === 3 && x.strikes === 2 },
];

const CatcherAccuracyChart = ({ base, data, onClick }) => {
    const { t: translate } = useTranslation();
    const [filters, setFilters] = useState({
        time: timeOptions(translate)[0],
        runners: runnersOptions(translate)[0],
        situation: situationOptions(translate)[0],
        result: resultOptions(translate)[0],
        dirtball: dirtballOptions(translate)[0],
        batter: batterOptions(translate)[0],
        count: countOptions(translate)[0],
    });
    const pitchVideoFn = usePitchVideoFn();
    const filteredData = filterThrows(data, base, filters);
    const seriesData = makeSeries(translate, filteredData, base);

    return (
        <>
            <StyledContainer>
                <CatcherAccuracy
                    base={base}
                    seriesData={seriesData}
                    enableZoom={false}
                    showlegend
                    pitchVideoFn={pitchVideoFn}
                    onClick={(p) => onClick(p)}
                />
            </StyledContainer>
            <StyledFiltersContainer>
                <StyledFiltersLabel>{translate('components.catcher-accuracy-chart.time')}:</StyledFiltersLabel>
                <Dropdown
                    options={timeOptions(translate)}
                    value={filters.time}
                    onSelect={(value) => setFilters({ ...filters, time: value })}
                />
                <StyledFiltersLabel>{translate('components.catcher-accuracy-chart.runners')}:</StyledFiltersLabel>
                <Dropdown
                    options={runnersOptions(translate).filter((o) => !o.base || o.base === base)}
                    value={filters.runners}
                    onSelect={(value) => setFilters({ ...filters, runners: value })}
                />
                <StyledFiltersLabel>{translate('components.catcher-accuracy-chart.situation')}:</StyledFiltersLabel>
                <Dropdown
                    options={situationOptions(translate)}
                    value={filters.situation}
                    onSelect={(value) => setFilters({ ...filters, situation: value })}
                />
                <StyledFiltersLabel>{translate('components.catcher-accuracy-chart.result')}:</StyledFiltersLabel>
                <Dropdown
                    options={resultOptions(translate)}
                    value={filters.result}
                    onSelect={(value) => setFilters({ ...filters, result: value })}
                />
            </StyledFiltersContainer>
            <StyledFiltersContainer>
                <StyledFiltersLabel>{translate('components.catcher-accuracy-chart.dirt-ball')}:</StyledFiltersLabel>
                <Dropdown
                    options={dirtballOptions(translate)}
                    value={filters.dirtball}
                    onSelect={(value) => setFilters({ ...filters, dirtball: value })}
                />
                <StyledFiltersLabel>{translate('baseball.batter')}:</StyledFiltersLabel>
                <Dropdown
                    options={batterOptions(translate)}
                    value={filters.batter}
                    onSelect={(value) => setFilters({ ...filters, batter: value })}
                />
                <StyledFiltersLabel>{translate('baseball.count')}:</StyledFiltersLabel>
                <Dropdown
                    options={countOptions(translate)}
                    value={filters.count}
                    onSelect={(value) => setFilters({ ...filters, count: value })}
                />
            </StyledFiltersContainer>
        </>
    );
};

CatcherAccuracyChart.propTypes = {
    base: PropTypes.number.isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    onClick: PropTypes.func,
};

CatcherAccuracyChart.defaultProps = {
    onClick: undefined,
};

export default CatcherAccuracyChart;
